import React, { useState, useEffect, useRef } from 'react';
import ButtonHoverPrimary from '../../../Buttons/ButtonHover/ButtonHoverPrimary';
import Icon from '../../../Icon/Icons';
import './CameraControls.scss';

// Toolbar with example navigation: https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/examples/toolbar/
// MDN toolbar documentation: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/toolbar_role

const CameraControls = ({ primaryColor, isOpen, onToggle, isMobile }) => {
  const [orbitControls, setOrbitControls] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(100);
  const [zoomAnnouncement, setZoomAnnouncement] = useState('');
  const [announcementTimeout, setAnnouncementTimeout] = useState(null);
  const [panAnnouncementTimeout, setPanAnnouncementTimeout] = useState(null);
  const [toggleFocusTimeout, setToggleFocusTimeout] = useState(null);
  const [focusedButtonIndex, setFocusedButtonIndex] = useState(0);
  const [toolbarHasFocus, setToolbarHasFocus] = useState(false);
  const [hoveringButton, setHoveringButton] = useState(null);
  const [panAnnouncement, setPanAnnouncement] = useState('');

  const sceneRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const buttonRefs = useRef([
    useRef(null), // zoom-in
    useRef(null), // zoom-out
    useRef(null), // pan-up
    useRef(null), // pan-down
    useRef(null), // pan-left
    useRef(null)  // pan-right
  ]);

  // global keyboard event listener to modify key handling when toolbar is focused
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      if (!isOpen || !toolbarHasFocus) return;

      const buttonCount = 6;

      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'Home':
        case 'End':
          event.preventDefault();
          event.stopPropagation();

          let newIndex = focusedButtonIndex;

          switch (event.key) {
            case 'ArrowUp':
              newIndex = focusedButtonIndex > 0 ? focusedButtonIndex - 1 : buttonCount - 1;
              break;
            case 'ArrowDown':
              newIndex = focusedButtonIndex < buttonCount - 1 ? focusedButtonIndex + 1 : 0;
              break;
            case 'ArrowLeft':
              newIndex = focusedButtonIndex > 0 ? focusedButtonIndex - 1 : buttonCount - 1;
              break;
            case 'ArrowRight':
              newIndex = focusedButtonIndex < buttonCount - 1 ? focusedButtonIndex + 1 : 0;
              break;
            case 'Home':
              newIndex = 0;
              break;
            case 'End':
              newIndex = buttonCount - 1;
              break;
            default:
              break;
          }

          setFocusedButtonIndex(newIndex);
          break;
        default:
          break;
      }

      // Handle Escape key to close toolbar
      if (event.key === 'Escape') {
        handleButtonBlur();
        onToggle();
        // Focus the toggle button after closing - use setTimeout to ensure DOM has updated
        const focusTimeout = setTimeout(() => {
          if (toggleButtonRef.current) {
            toggleButtonRef.current.focus();
          }
        }, 0);
        setToggleFocusTimeout(focusTimeout);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [isOpen, toolbarHasFocus, focusedButtonIndex]);

  useEffect(() => {
    if (focusedButtonIndex !== null) {
      const targetButton = buttonRefs.current[focusedButtonIndex]?.current;
      if (targetButton) {
        targetButton.focus();
      }
    }
  }, [focusedButtonIndex]);

  useEffect(() => {
    // find camera and set up orbit controls
    const setupCameraControls = (sceneElement) => {
      const camera = sceneElement.querySelector('[camera]');
      if (!camera?.components['orbit-controls']) {
        return false;
      }

      const controls = camera.components['orbit-controls'];
      setOrbitControls(controls);

      if (controls.camera) {
        const initialZoom = Math.round(controls.camera.zoom * 100);
        setCurrentZoom(initialZoom);
      }
      return true;
    };

    const handleSceneLoaded = (sceneElement) => {
      setupCameraControls(sceneElement);
    };

    const setupEventListeners = (sceneElement) => {
      const sceneLoadedHandler = () => handleSceneLoaded(sceneElement);

      sceneElement.addEventListener('loaded', sceneLoadedHandler);

      return () => sceneElement.removeEventListener('loaded', sceneLoadedHandler);
    };

    // find scene and set up event listeners
    const sceneElement = document.querySelector('a-scene');
    if (sceneElement) {
      sceneRef.current = sceneElement;
      return setupEventListeners(sceneElement);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (announcementTimeout) {
        clearTimeout(announcementTimeout);
      }
        if (panAnnouncementTimeout) {
        clearTimeout(panAnnouncementTimeout);
      }
      if (toggleFocusTimeout) {
        clearTimeout(toggleFocusTimeout);
      }
    };
  }, [announcementTimeout, panAnnouncementTimeout, toggleFocusTimeout]);

  // re-establish orbit controls connection if it becomes null (e.g., after scene changes)
  useEffect(() => {
    if (!orbitControls) {
      const sceneElement = document.querySelector('a-scene');
      if (sceneElement) {
        sceneRef.current = sceneElement;
        const camera = sceneElement.querySelector('[camera]');
        if (camera?.components['orbit-controls']) {
          const controls = camera.components['orbit-controls'];
          setOrbitControls(controls);

          if (controls.camera) {
            const initialZoom = Math.round(controls.camera.zoom * 100);
            setCurrentZoom(initialZoom);
          }
        }
      }
    }
  }, [orbitControls]);

  const announceZoom = (newZoom) => {
    if (announcementTimeout) {
      clearTimeout(announcementTimeout);
    }
    setZoomAnnouncement(`Zoom level ${newZoom}%`);
    const timeout = setTimeout(() => setZoomAnnouncement(''), 1000);
    setAnnouncementTimeout(timeout);
  };

  // announce panning direction, alternating period
  // `aria-live` won't announce same text twice, even if cleared before adding text
  // e.g. users should hear "panning up" on each button press, instead of just the first press
  const announcePan = (direction) => {
    if (panAnnouncementTimeout) {
      clearTimeout(panAnnouncementTimeout);
    }

    const timeout = setTimeout(() => {
      const hasPeriod = panAnnouncement.includes('.');
      const message = hasPeriod ? `Panning ${direction}` : `Panning ${direction}.`;
      setPanAnnouncement(message);
    }, 100);

    setPanAnnouncementTimeout(timeout);
  }

  const handleZoomIn = () => {
    if (orbitControls) {
      orbitControls.zoomIn(0.1);

      if (orbitControls.camera) {
        const actualZoom = Math.round(orbitControls.camera.zoom * 100);
        if (actualZoom !== currentZoom) {
          setCurrentZoom(actualZoom);
          announceZoom(actualZoom);
        }
      }
    }
  };

  const handleZoomOut = () => {
    if (orbitControls) {
      orbitControls.zoomOut(0.1);

      if (orbitControls.camera) {
        const actualZoom = Math.round(orbitControls.camera.zoom * 100);
        if (actualZoom !== currentZoom) {
          setCurrentZoom(actualZoom);
          announceZoom(actualZoom);
        }
      }
    }
  };

  const handleOrbit = (direction) => {
    if (orbitControls) {
      const orbitAmount = 0.08; // orbit speed

      switch (direction) {
        case 'up':
          orbitControls.rotateUp(-orbitAmount);
          announcePan('up');
          break;
        case 'down':
          orbitControls.rotateUp(orbitAmount);
          announcePan('down');
          break;
        case 'left':
          orbitControls.rotateLeft(-orbitAmount);
          announcePan('left');
          break;
        case 'right':
          orbitControls.rotateLeft(orbitAmount);
          announcePan('right');
          break;
        default:
          break;
      }

      if (sceneRef.current) {
        sceneRef.current.emit('user-interaction-detected', null, false);
      }

      orbitControls.updateView();
    }
  };

  const getTabIndex = (buttonIndex) => {
    return focusedButtonIndex === buttonIndex ? 0 : -1;
  };

  // ButtonHoverRender only works with natural tab navigation, so we need to handle focus manually
  const handleButtonFocus = (index) => {
    setFocusedButtonIndex(index);
    setToolbarHasFocus(true);

    // trigger tooltip by setting hover state when button receives focus
    const buttonIds = ['zoom-in', 'zoom-out', 'pan-up', 'pan-down', 'pan-left', 'pan-right'];
    const buttonId = buttonIds[index];
    if (buttonId) {
      setHoveringButton(buttonId);
    }
  };

  const handleButtonBlur = () => {
    setToolbarHasFocus(false);
    setHoveringButton(null);
  };

  const handleButtonHover = (buttonId) => {
    setHoveringButton(buttonId);
  };

  const handleButtonLeave = () => {
    setHoveringButton(null);
  };

  const isButtonHovering = (buttonId) => {
    return hoveringButton === buttonId;
  };

  return (
    <div className="camera-controls-container">
      {/* Screen reader announcement for zoom changes */}
      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        tabIndex={-1}
      >
        {zoomAnnouncement}
      </div>
      {/* Screen reader announcement for pan changes */}
      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        tabIndex={-1}
      >
        {panAnnouncement}
      </div>

      <ButtonHoverPrimary
        myRef={toggleButtonRef}
        onEnter={() => handleButtonHover('toggle')}
        onLeave={handleButtonLeave}
        hovering={isButtonHovering('toggle')}
        active={isOpen}
        isMobile={isMobile}
        className="camera-controls__toggle-btn"
        color={primaryColor}
        backgroundColor="#fff"
        handleClick={onToggle}
        tooltip={{
          text: 'Camera Controls',
          node: '.camera-controls__toggle-btn',
          position: 'bottom-right',
        }}
        tooltipClass={'camera-controls__toggle-tooltip'}
        data-cy="camera-controls-btn"
        aria-expanded={isOpen}
        aria-label={isOpen ? "Close camera controls" : "Open camera controls"}
      >
        <Icon>{isOpen ? 'close' : 'open_with'}</Icon>
      </ButtonHoverPrimary>

      {isOpen && (
        <div
          className={`camera-controls-panel ${toolbarHasFocus ? 'focus' : ''}`}
          role="toolbar"
          aria-label="Camera controls"
          aria-orientation="vertical"
          aria-controls="scene"
        >
          <div className="zoom-controls">
            <ButtonHoverPrimary
              myRef={buttonRefs.current[0]}
              onEnter={() => handleButtonHover('zoom-in')}
              onLeave={handleButtonLeave}
              hovering={isButtonHovering('zoom-in')}
              isMobile={isMobile}
              className="camera-controls__zoom-in-btn"
              color={primaryColor}
              backgroundColor="#fff"
              handleClick={handleZoomIn}
              onFocus={() => handleButtonFocus(0)}
              onBlur={handleButtonBlur}
              tabIndex={getTabIndex(0)}
              tooltip={{
                text: 'Zoom In',
                node: '.camera-controls__zoom-in-btn',
                position: 'left',
              }}
              tooltipClass={'camera-controls__zoom-in-tooltip'}
              data-cy="camera-controls-btn-zoom-in"
              aria-label='Zoom in'
            >
              <Icon>add</Icon>
            </ButtonHoverPrimary>
            <ButtonHoverPrimary
              myRef={buttonRefs.current[1]}
              onEnter={() => handleButtonHover('zoom-out')}
              onLeave={handleButtonLeave}
              hovering={isButtonHovering('zoom-out')}
              isMobile={isMobile}
              className="camera-controls__zoom-out-btn"
              color={primaryColor}
              backgroundColor="#fff"
              handleClick={handleZoomOut}
              onFocus={() => handleButtonFocus(1)}
              onBlur={handleButtonBlur}
              tabIndex={getTabIndex(1)}
              tooltip={{
                text: 'Zoom Out',
                node: '.camera-controls__zoom-out-btn',
                position: 'left',
              }}
              tooltipClass={'camera-controls__zoom-out-tooltip'}
              data-cy="camera-controls-btn-zoom-out"
              aria-label='Zoom out'
            >
              <Icon>remove</Icon>
            </ButtonHoverPrimary>
          </div>
          <div className="pan-controls">
            <ButtonHoverPrimary
              myRef={buttonRefs.current[2]}
              onEnter={() => handleButtonHover('pan-up')}
              onLeave={handleButtonLeave}
              hovering={isButtonHovering('pan-up')}
              isMobile={isMobile}
              className="camera-controls__pan-up-btn"
              color={primaryColor}
              backgroundColor="#fff"
              handleClick={() => handleOrbit('up')}
              onFocus={() => handleButtonFocus(2)}
              onBlur={handleButtonBlur}
              tabIndex={getTabIndex(2)}
              tooltip={{
                text: 'Pan Up',
                node: '.camera-controls__pan-up-btn',
                position: 'left',
              }}
              tooltipClass={'camera-controls__pan-up-tooltip'}
              data-cy="camera-controls-btn-pan-up"
              aria-label="Pan up"
            >
              <Icon>keyboard_arrow_up</Icon>
            </ButtonHoverPrimary>
            <ButtonHoverPrimary
              myRef={buttonRefs.current[3]}
              onEnter={() => handleButtonHover('pan-down')}
              onLeave={handleButtonLeave}
              hovering={isButtonHovering('pan-down')}
              isMobile={isMobile}
              className="camera-controls__pan-down-btn"
              color={primaryColor}
              backgroundColor="#fff"
              handleClick={() => handleOrbit('down')}
              onFocus={() => handleButtonFocus(3)}
              onBlur={handleButtonBlur}
              tabIndex={getTabIndex(3)}
              tooltip={{
                text: 'Pan Down',
                node: '.camera-controls__pan-down-btn',
                position: 'left',
              }}
              tooltipClass={'camera-controls__pan-down-tooltip'}
              data-cy="camera-controls-btn-pan-down"
              aria-label="Pan down"
            >
              <Icon>keyboard_arrow_down</Icon>
            </ButtonHoverPrimary>
            <ButtonHoverPrimary
              myRef={buttonRefs.current[4]}
              onEnter={() => handleButtonHover('pan-left')}
              onLeave={handleButtonLeave}
              hovering={isButtonHovering('pan-left')}
              isMobile={isMobile}
              className="camera-controls__pan-left-btn"
              color={primaryColor}
              backgroundColor="#fff"
              handleClick={() => handleOrbit('left')}
              onFocus={() => handleButtonFocus(4)}
              onBlur={handleButtonBlur}
              tabIndex={getTabIndex(4)}
              tooltip={{
                text: 'Pan Left',
                node: '.camera-controls__pan-left-btn',
                position: 'left',
              }}
              tooltipClass={'camera-controls__pan-left-tooltip'}
              data-cy="camera-controls-btn-pan-left"
              aria-label="Pan left"
            >
              <Icon>keyboard_arrow_left</Icon>
            </ButtonHoverPrimary>
            <ButtonHoverPrimary
              myRef={buttonRefs.current[5]}
              onEnter={() => handleButtonHover('pan-right')}
              onLeave={handleButtonLeave}
              hovering={isButtonHovering('pan-right')}
              isMobile={isMobile}
              className="camera-controls__pan-right-btn"
              color={primaryColor}
              backgroundColor="#fff"
              handleClick={() => handleOrbit('right')}
              onFocus={() => handleButtonFocus(5)}
              onBlur={handleButtonBlur}
              tabIndex={getTabIndex(5)}
              tooltip={{
                text: 'Pan Right',
                node: '.camera-controls__pan-right-btn',
                position: 'left',
              }}
              tooltipClass={'camera-controls__pan-right-tooltip'}
              data-cy="camera-controls-btn-pan-right"
              aria-label="Pan right"
            >
              <Icon>keyboard_arrow_right</Icon>
            </ButtonHoverPrimary>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraControls;  